import { useEffect, useRef, useState } from "react";
import buildVersion from "@/build-version.json";

const POLL_MS = 2 * 60 * 1000; // every 2 minutes
const VERSION_URL = `${process.env.PUBLIC_URL || ""}/version.json`;

/**
 * Polls /version.json (with no-store) and detects when the deployed PWA bundle
 * is newer than the one currently running in the browser.
 *
 * Returns: { current, latest, outdated }
 *  - current : the version baked at build time (compiled into the JS bundle).
 *  - latest  : the most recent version found on the server (or null until first fetch).
 *  - outdated: true when latest.build_id !== current.build_id.
 */
export default function useVersionCheck() {
  const current = buildVersion;
  const [latest, setLatest] = useState(null);
  const lastFetch = useRef(0);

  useEffect(() => {
    let stopped = false;

    const fetchOnce = async () => {
      const now = Date.now();
      if (now - lastFetch.current < 5000) return; // throttle
      lastFetch.current = now;
      try {
        const r = await fetch(VERSION_URL, {
          cache: "no-store",
          headers: { "Cache-Control": "no-cache" },
        });
        if (!r.ok) return;
        const data = await r.json();
        if (!stopped && data?.build_id) setLatest(data);
      } catch {
        /* offline, ignore */
      }
    };

    fetchOnce();
    const intv = setInterval(fetchOnce, POLL_MS);
    const onVis = () => {
      if (document.visibilityState === "visible") fetchOnce();
    };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", fetchOnce);

    return () => {
      stopped = true;
      clearInterval(intv);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", fetchOnce);
    };
  }, []);

  const outdated = !!(latest && latest.build_id && latest.build_id !== current.build_id);
  return { current, latest, outdated };
}
